import * as React from 'react';
import { useCallback } from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import {
  ImageAddMethod,
  ImageUploadContext,
} from '~analytics/adapters/ImageEventAdapterTypes';
import { PaywallScreens } from '~analytics/types';
import ImageFieldDisplay from '~shared/components/ImageField/ImageFieldDisplay';
import ImagePreviewer from '~shared/components/ImageField/ImagePreviewer';
import QuillEditor from '~shared/components/quill/QuillEditor';
import { QuillTextChangeHandler } from '~shared/components/quill/quillHooks';
import { RichTextRenderer } from '~shared/components/RichTextRenderer';
import { InfoIcon } from '~teacher/components/QuizQuestion/InfoIcon';
import { useUploadImage } from '~teacher/hook/upload/useUploadImage';
import { ImageResource } from '~teacher/models/quizzes';
import { proCheck } from '~teacher/util/proCheck';

import {
  updateQuestionExplanation,
  updateQuestionExplanationImage,
} from '../../slices/quiz/quizSlice';
import { openPaywall } from '../../slices/teacher/teacherSlice';

interface Props {
  questionId: number | string;
  content: string;
  image: ImageResource;
  /** Is the question in Edit mode? */
  isEditing: boolean;
  /** Is the question in Disabled mode? */
  isDisabled: boolean;
}

export function QuestionDetails(props: Props) {
  const { content, image, isEditing, isDisabled } = props;
  const dispatch = useDispatch();

  const editorRef = React.useRef<HTMLDivElement>(null);
  const uploadImage = useUploadImage({
    context: ImageUploadContext.Explanation,
  });

  const handleUploadImage = useCallback(
    (file: File) => {
      uploadImage.mutate(
        {
          files: [file],
          addMethod: ImageAddMethod.CopyPaste,
        },
        {
          onSuccess: (url) => {
            dispatch(updateQuestionExplanationImage({ url }));
          },
        }
      );
    },
    [dispatch, uploadImage]
  );

  useHandlePaste({
    quillRef: editorRef,
    isDisabled: isDisabled,
    handleUploadImage: handleUploadImage,
  });

  const showAdvancedQuizEditor = proCheck.canShowAdvancedQuizEditor();
  const handleTextChange = React.useCallback<QuillTextChangeHandler>(
    ({ html }: { html: string; text: string }) => {
      const explanationText = html;
      if (isEditing) {
        dispatch(updateQuestionExplanation({ explanationText }));
      }
    },
    [dispatch, isEditing]
  );
  const handleImageChange = React.useCallback(
    (url) => {
      dispatch(updateQuestionExplanationImage({ url }));
    },
    [dispatch]
  );

  const isEmpty = ['', null].includes(content?.trim());
  const shouldRender = isEditing || !isEmpty || image;
  const imageFieldLabel = translate('Explanation Image');
  const imageClearLabel = translate('Remove Image');

  const explanationClass = React.useMemo(
    () =>
      cx('grid grid-cols-question-fields-question-editor max-w-5xl p-0', {
        'mt-4': !isEditing,
        'mt-8': isEditing,
      }),
    [isEditing]
  );

  return (
    shouldRender && (
      <div className={explanationClass} data-quiz-question-details>
        <div className=" flex items-center justify-center  col-start-2 w-12 h-12">
          <InfoIcon
            hasOutline={!isEditing}
            a11yLabel={translate('Explanation (Optional)')}
          />
        </div>
        <div className="col-start-3 min-w-0 ml-2">
          {!isEditing || isDisabled ? (
            <div
              className={`relative flex-1 max-w-full min-w-0 quill-field is-readonly ${
                image && 'is-image-set'
              }}`}
            >
              <div className="relative z-10 flex-1 max-w-full py-2 quill-wrapper">
                {content ? (
                  <RichTextRenderer
                    className="font-sans text-blue-900 text-base leading-8 ql-editor"
                    htmlText={content}
                  />
                ) : null}
                {image ? (
                  <ImagePreviewer
                    containerStyle="inline-flex my-2 rounded-1"
                    imageStyle="max-h-64 rounded-1"
                    imageURL={image?.urlS}
                    showOutline
                    allowZoom
                    zoomImageURL={image?.url}
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <QuillEditor
              initialHtml={content}
              placeholder={translate('An explanation, if you like.')}
              onTextChange={handleTextChange}
              isReadOnly={!isEditing || isDisabled}
              ref={editorRef}
            >
              <ImageFieldDisplay
                key={image?.id}
                context={ImageUploadContext.Explanation}
                onImageChange={handleImageChange}
                isReadOnly={!isEditing || isDisabled}
                image={image}
                imageFieldLabel={imageFieldLabel}
                imageClearLabel={imageClearLabel}
                imageFieldDisabled={isDisabled}
                addImageIntercept={
                  !showAdvancedQuizEditor
                    ? () =>
                        dispatch(
                          openPaywall({
                            paywallTouch: [
                              PaywallScreens.QuizEditAddExplanationImage,
                            ],
                          })
                        )
                    : null
                }
              />
            </QuillEditor>
          )}
        </div>
      </div>
    )
  );
}

function useHandlePaste({
  quillRef,
  isDisabled,
  handleUploadImage,
}: {
  handleUploadImage: (file: File) => void;
  isDisabled: boolean;
  quillRef: React.RefObject<HTMLDivElement>;
}) {
  React.useEffect(() => {
    const editor = quillRef.current;
    const handlePaste = async (e: ClipboardEvent) => {
      if (isDisabled) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            try {
              handleUploadImage(file);
            } catch (error) {
              console.error('Failed to upload pasted image:', error);
            }
          }
        }
      }
    };
    editor?.addEventListener('paste', handlePaste);
    return () => editor?.removeEventListener('paste', handlePaste);
  }, [handleUploadImage, isDisabled, quillRef]);
}
